import { bbox, toLocal, signedArea } from "./geometry";

// Trim knife: a straight cut through a decal along the infinite line a→b (local piece coords). Both sides survive as their
// own pieces. Holes are clipped the same way (a hole cut open along the line stays open at the cut edge).
const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
const sideOf = (a, b) => ([x, y]) => (b[0] - a[0]) * (y - a[1]) - (b[1] - a[1]) * (x - a[0]);

export function clipLine(pts, a, b, keepLeft) {
  const s = sideOf(a, b), eps = 1e-9;
  const inside = (p) => (keepLeft ? s(p) >= -eps : s(p) <= eps);
  const cross = (p, q) => { const sp = s(p), sq = s(q); return lerp(p, q, sp / (sp - sq || 1e-12)); };
  const out = [];
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length], ip = inside(p), iq = inside(q);
    if (ip) { out.push(p); if (!iq) out.push(cross(p, q)); } else if (iq) out.push(cross(p, q));
  }
  return out;
}

const MIN_AREA = 0.02; // sq in — anything smaller is a sliver, not a piece

// Both sides of the cut for a piece on the roll; aW / bW are world points. null when the line misses the decal.
export function knifeCut(piece, aW, bW) {
  const a = toLocal(piece, aW), b = toLocal(piece, bW);
  if (Math.hypot(b[0] - a[0], b[1] - a[1]) < 1e-6) return null;
  const side = (keepLeft) => {
    const points = clipLine(piece.points, a, b, keepLeft);
    if (points.length < 3 || Math.abs(signedArea(points)) < MIN_AREA) return null;
    const holes = (piece.holes || []).map((h) => clipLine(h, a, b, keepLeft)).filter((h) => h.length >= 3 && Math.abs(signedArea(h)) > 1e-6);
    return { points, holes };
  };
  const L = side(true), R = side(false);
  if (!L || !R) return null;
  return { L, R };
}

// The knife line stretched well past the decal so it reads as a full cut (world coords).
export function knifeLine(aW, bW, worldPts) {
  const bb = bbox(worldPts), reach = Math.hypot(bb.w, bb.h) + 2;
  const d = Math.hypot(bW[0] - aW[0], bW[1] - aW[1]) || 1, ux = (bW[0] - aW[0]) / d, uy = (bW[1] - aW[1]) / d;
  const m = [(aW[0] + bW[0]) / 2, (aW[1] + bW[1]) / 2];
  return [[m[0] - ux * reach, m[1] - uy * reach], [m[0] + ux * reach, m[1] + uy * reach]];
}

// Shift: snap the line angle to 15° steps around the first point
export function snapAngle(a, b, step = 15) {
  const d = Math.hypot(b[0] - a[0], b[1] - a[1]), ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const s = (Math.round((ang * 180) / Math.PI / step) * step * Math.PI) / 180;
  return [a[0] + Math.cos(s) * d, a[1] + Math.sin(s) * d];
}
